import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { fetchBlog } from "@/lib/api";
import BlogBody from "@/components/BlogBody";
import { DIET } from "@/constants/testIds";

export default function DietBlogDetail() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchBlog(id)
      .then((d) => alive && setBlog(d))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20 flex justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </main>
    );
  }

  if (error || !blog) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20">
        <Link
          to="/diet"
          data-testid={DIET.blogBack}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ChevronLeft size={16} /> Back to diet
        </Link>
        <h1 className="font-serif-display text-4xl text-slate-900 mt-6">
          Article not found
        </h1>
      </main>
    );
  }

  return (
    <main data-testid={DIET.blogDetail} className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-20">
      <Link
        to="/diet"
        data-testid={DIET.blogBack}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ChevronLeft size={16} /> Back to diet
      </Link>

      <Badge
        variant="secondary"
        className="bg-emerald-50 text-slate-700 border-emerald-100 mt-7"
      >
        Diet
      </Badge>

      <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-slate-900 mt-4 leading-tight">
        {blog.title}
      </h1>

      {blog.excerpt ? (
        <p className="text-slate-600 mt-5 text-lg leading-relaxed">{blog.excerpt}</p>
      ) : null}

      {blog.cover_image ? (
        <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
          <img
            src={blog.cover_image}
            alt={blog.title}
            className="w-full h-auto object-cover"
          />
        </div>
      ) : null}

      {blog.tags && blog.tags.length > 0 ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {blog.tags.map((t) => (
            <span
              key={t}
              className="text-[11px] uppercase tracking-wider text-slate-500"
            >
              #{t}
            </span>
          ))}
        </div>
      ) : null}

      <article className="mt-10">
        <BlogBody html={blog.body} />
      </article>
    </main>
  );
}
